<script setup>
import { RouterView, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { defineAsyncComponent, onMounted, onUnmounted } from 'vue'
import Navbar from '@/components/layout/Navbar.vue'
import Footer from '@/components/layout/Footer.vue'
import CookieBanner from '@/components/layout/CookieBanner.vue'

// Carregados apenas sob demanda: não fazem parte do bundle inicial
const DemoModal = defineAsyncComponent(() => import('@/components/demo/DemoModal.vue'))
const ConsultorModal = defineAsyncComponent(() => import('@/components/home/ConsultorModal.vue'))
const ChatAssistant = defineAsyncComponent(() => import('@/components/home/ChatAssistant.vue'))
const WebsiteFlowWidget = defineAsyncComponent(() => import('@/components/home/WebsiteFlowWidget.vue'))
import { useModalStore } from '@/stores/useModalStore'
import { useCookieStore } from '@/stores/useCookieStore'
import { initGtm } from '@/composables/useGtm'

const router = useRouter()
const modalStore = useModalStore()
const cookieStore = useCookieStore()

const widgetFlowId = String(import.meta.env.VITE_WEBSITE_FLOW_ID || '').trim()

// Permite que quem dispara o evento sugira o segmento já selecionado no
// modal (ex.: CustomEvent('open-demo-modal', { detail: { segmento: '...' } })).
const handleOpenDemoModal = (e) => modalStore.openDemo(e?.detail?.segmento)

onMounted(() => {
  router.isReady().catch((error) => {
    console.error('Erro ao inicializar o router:', error)
  })

  cookieStore.initFromStorage()
  initGtm(router)
  window.addEventListener('open-demo-modal', handleOpenDemoModal)
  window.addEventListener('open-consultor-modal', modalStore.openConsultor)
  window.addEventListener('cookies-accepted', () => cookieStore.enable('cookies-accepted'))
  window.addEventListener('cookies-rejected', () => cookieStore.enable('cookies-rejected'))
  window.addEventListener('banner-closed', () => cookieStore.enable('banner-closed'))
})

onUnmounted(() => {
  window.removeEventListener('open-demo-modal', handleOpenDemoModal)
  window.removeEventListener('open-consultor-modal', modalStore.openConsultor)
  window.removeEventListener('cookies-accepted', () => cookieStore.enable('cookies-accepted'))
  window.removeEventListener('cookies-rejected', () => cookieStore.enable('cookies-rejected'))
  window.removeEventListener('banner-closed', () => cookieStore.enable('banner-closed'))
})

// Defaults globais de head (antes em main.js). Páginas com useSeo sobrescrevem
// title/description; estes valem como fallback e são congelados na build SSG.
useHead({
  htmlAttrs: { lang: 'pt-BR', class: 'scroll-smooth' },
  bodyAttrs: { class: 'antialiased' },
  title: 'Kali Sistemas - Soluções em Gestão Empresarial',
  meta: [
    {
      name: 'description',
      content:
        'Sistemas inteligentes para gestão empresarial. Simplifique processos, aumente a produtividade e tome decisões mais assertivas com a Kali Sistemas.',
    },
    {
      name: 'keywords',
      content:
        'gestão empresarial, sistema erp, software de gestão, automação comercial, gestão financeira, controle de estoque',
    },
  ],
  link: [{ rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' }],
})
</script>

<template>
  <div class="flex flex-col min-h-screen">
    <Navbar />
    <main class="flex-1 mt-16">
      <RouterView v-slot="{ Component }">
        <Suspense>
          <Transition name="fade" mode="out-in" appear>
            <div :key="$route.fullPath" class="route-container">
              <component :is="Component" />
            </div>
          </Transition>
          <template #fallback>
            <div class="min-h-[40vh] flex items-center justify-center">
              <div class="h-6 w-6 rounded-full border-2 border-gray-300 border-t-[#006086] animate-spin" aria-label="Carregando" />
            </div>
          </template>
        </Suspense>
      </RouterView>
    </main>

    <Footer />
    <CookieBanner />

    <WebsiteFlowWidget v-if="cookieStore.canLoadChat && widgetFlowId" />
    <ChatAssistant v-else-if="cookieStore.canLoadChat" />

    <DemoModal />
    <ConsultorModal />
  </div>
</template>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

body {
  padding-bottom: env(safe-area-inset-bottom);
}
</style>
